import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import TenantActivation from './components/TenantActivation';
import TenantResetPassword from './components/TenantResetPassword';
import TenantPortal from './components/TenantPortal';
import PortalTokenApp from './components/communications/PortalTokenApp';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useThemeContext } from './context/ThemeContext';
import { primaryShades, readableOn, UI_CONTRAST } from './styles/contrast';

// Log React version for security verification
console.log(`⚛️  React Version: ${React.version} (Security Update: CVE-2025-55182, CVE-2025-55184, CVE-2025-67779, CVE-2025-55183 patched in 19.2.3+)`);

// Component to create MUI theme based on custom palette
const ThemedApp = ({ children }) => {
  const { currentPalette } = useThemeContext();
  // Pale primaries (e.g. mint) fill buttons and chips fine but are unreadable as text on white
  const primaryOnLight = primaryShades(currentPalette.colors);
  const infoIcon = readableOn(
    currentPalette.colors.info,
    [currentPalette.colors.background, currentPalette.colors.surface],
    UI_CONTRAST
  );
  
  const muiTheme = createTheme({
    palette: {
      primary: {
        main: currentPalette.colors.primary,
      },
      secondary: {
        main: currentPalette.colors.secondary,
      },
      error: {
        main: currentPalette.colors.error,
      },
      warning: {
        main: currentPalette.colors.warning,
      },
      info: {
        main: currentPalette.colors.info,
      },
      success: {
        main: currentPalette.colors.success,
      },
      background: {
        default: currentPalette.colors.background,
        paper: currentPalette.colors.surface,
      },
      text: {
        primary: currentPalette.colors.text.primary,
        secondary: currentPalette.colors.text.secondary,
      },
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      h1: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.03em',
        lineHeight: 1.15,
      },
      h2: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.025em',
        lineHeight: 1.2,
      },
      h3: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.25,
      },
      h4: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.25,
      },
      h5: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: '-0.015em',
      },
      h6: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
      },
      subtitle1: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 500,
        lineHeight: 1.6,
      },
      subtitle2: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 500,
        lineHeight: 1.6,
      },
      body1: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 400,
        lineHeight: 1.65,
      },
      body2: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 400,
        lineHeight: 1.6,
      },
      button: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        textTransform: 'none',
      },
      caption: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      },
      overline: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: '0.06em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            textTransform: 'none',
          },
          containedPrimary: {
            // Stronger CTA presence; still uses palette primary color
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              filter: 'brightness(0.94)',
            },
          },
          outlinedPrimary: {
            borderWidth: 1.5,
            color: primaryOnLight.text,
            borderColor: primaryOnLight.ui,
            '&:hover': {
              borderWidth: 1.5,
              borderColor: primaryOnLight.text,
            },
          },
          textPrimary: {
            fontWeight: 600,
            color: primaryOnLight.text,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          outlinedPrimary: {
            color: primaryOnLight.text,
            borderColor: primaryOnLight.ui,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState }) =>
            (ownerState.color || ownerState.severity) === 'info' && ownerState.variant !== 'filled'
              ? { '& .MuiAlert-icon': { color: infoIcon } }
              : {},
        },
      },
      MuiTab: {
        styleOverrides: {
          textColorPrimary: {
            '&.Mui-selected': {
              color: primaryOnLight.text,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState }) => (ownerState.color === 'primary' ? { color: primaryOnLight.text } : {}),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ ownerState }) =>
            !ownerState.color || ownerState.color === 'primary' ? { color: primaryOnLight.text } : {},
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 10,
            marginLeft: 8,
            marginRight: 8,
            marginBottom: 4,
            '&.Mui-selected': {
              backgroundColor:
                theme.palette.mode === 'light'
                  ? `${theme.palette.primary.main}22`
                  : `${theme.palette.primary.main}33`,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? `${theme.palette.primary.main}33`
                    : `${theme.palette.primary.main}44`,
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
              },
              '& .MuiListItemText-primary': {
                fontWeight: 600,
              },
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
            color: 'var(--color-text-secondary, #6B7280)',
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorPrimary: {
            color: primaryOnLight.ui,
          },
          fontSizeSmall: {
            fontSize: '1.125rem',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 16,
          },
        },
      },
    },
  });

  return (
    <MuiThemeProvider theme={muiTheme}>
      <style>{`
        html, body, #root {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        }
      `}</style>
      {children}
    </MuiThemeProvider>
  );
};

// Check for tenant routes (use startsWith for robustness: trailing slash, subpaths)
const pathname = window.location.pathname;
const isActivationRoute = pathname === '/tenant-portal/activate' || pathname.startsWith('/tenant-portal/activate/');
const isResetPasswordRoute = pathname === '/tenant-portal/reset-password' || pathname.startsWith('/tenant-portal/reset-password/');
const isTenantPortalRoute = pathname === '/tenant-portal' || pathname.startsWith('/tenant-portal/');
// ZRF-WTPB-001 — public token shells (not TenantPortal)
const isPortalTokenRoute = pathname === '/p/t' || pathname.startsWith('/p/t/');

const root = ReactDOM.createRoot(document.getElementById('root'));

// Render appropriate component based on route
// Check activation and reset-password first (more specific) - then tenant portal / portal tokens
let component = <App />;
if (isActivationRoute) {
  component = <TenantActivation />;
} else if (isResetPasswordRoute) {
  component = <TenantResetPassword />;
} else if (isTenantPortalRoute) {
  component = <TenantPortal />;
} else if (isPortalTokenRoute) {
  component = <PortalTokenApp />;
}

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <ThemedApp>
          <AuthProvider>{component}</AuthProvider>
        </ThemedApp>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
